import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { TonsPerYardService } from 'src/app/_services/tons-per-yard/tons-per-yard.service';
import { Entry, LocationInterface, LocationWithFilter, OutputType } from 'src/app/interfaces/lotes.interfaces';
import { TonsPerYard } from 'src/app/interfaces/tons-per-yard.interfaces';

/**
 * Componente que gestiona la asignación o salida de tonelaje y piezas por
 * ubicación (patio) mediante un formulario dinámico, calculando los montos
 * pendientes por asignar y filtrando las ubicaciones disponibles.
 */
@Component({
  selector: 'app-amounts-by-input-locations',
  templateUrl: './amounts-by-input-locations.component.html',
  styles: [
  ]
})
export class AmountsByInputLocationsComponent implements OnInit, OnChanges {
  @Input() containerStyles = {
    'grid-template-columns': 'repeat(4, 1fr)'
  };
  @Input() movimiento: string = 'crear';
  @Input() entryTonnage: number | string = 0;
  @Input() entryPieces: number | string = 0;
  @Input() locations: LocationInterface[] = [];
  @Input() amountsPerYardList: TonsPerYard[] = [];
  @Input() entryDetail: Entry[] = [];
  @Input() outputDetail: OutputType[] = [];
  @Input() locationsStatus: boolean = true;
  isOutput: boolean = this.router.url.includes('output');
  locationsFiltered: LocationWithFilter[] = [];
  amountsToBeAllocated = {
    tons: 0,
    tonsPercentage: 0,
    pieces: 0,
    piecesPercentage: 0
  };
  locationForm = this.fb.group<any>({
    location_list : this.fb.array([this.createLocationGroup()])
  });
  loading: boolean = false;
  overlay: boolean = false;
  message: string = '';
  icon: string = '';
  texts = {
    title: this.isOutput ? 'Sacar tonelaje y piezas de patios' : 'Asignar tonelaje y piezas en patios',
    tonsToBeAssigned: this.isOutput ? 'Toneladas por sacar' : 'Toneladas por asignar',
    piecesToBeAssigned: this.isOutput ? 'Piezas por sacar' : 'Piezas por asignar',
    locationLabel: this.isOutput ? 'Ubicación' : 'Ubicación de entrada*',
    tonnageLabel: this.isOutput ? 'Tonelaje que salen*' : 'Tonelaje que entra*',
    piecesLabel: this.isOutput ? 'Piezas que salen*' : 'Piezas que entran*'
  };

  constructor(
    private fb: FormBuilder,
    private amountsPerYardSvc: TonsPerYardService,
    private router: Router,
  ) {}

  get locationArray() {
    return this.locationForm.get('location_list') as FormArray;
  }

  /**
   * Recalcula montos y ubicaciones cuando cambian las entradas de tonelaje/piezas o la lista de ubicaciones.
   * @param changes - Cambios detectados en las propiedades de entrada del componente.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entryTonnage'] || changes['entryPieces']) {
      this.setAmountsToBeAllocated();
      this.setLocationsArray();
      this.amountsPerYardSvc.assignedTons = (Number(this.amountsToBeAllocated.tons) === 0 && Number(this.amountsToBeAllocated.tonsPercentage) === 0) ? true : false;
      this.amountsPerYardSvc.assignedPieces = (Number(this.amountsToBeAllocated.pieces) === 0 && Number(this.amountsToBeAllocated.piecesPercentage) === 0) ? true : false;
      if (changes['entryPieces']) {
        this.locationArray.value.forEach((location: any, i: number) => {
          if (Number(this.entryPieces) === 0 || this.movimiento === 'detalle') {
            this.locationForm.get(`location_list.${i}.pieces`)!.disable();
          } else {
            this.locationForm.get(`location_list.${i}.pieces`)!.enable();
          }
        });
      }
      this.setTonnageAndPieces();
    }

    if (changes['locations']) {
      this.locationsFiltered = [...this.locations.map( location => ({
        ...location,
        disabled: false
      }))];
      if (this.isOutput) {
        // Id´s de las ubicaciones con tonelaje/piezas
        const idList: number[] = [];
        this.amountsPerYardList.forEach(location => {
          idList.push(location.id);
        });
        // Filtrar ubicaciones con tonelaje/piezas
        if (this.movimiento === 'crear') {
          this.locationsFiltered = [...this.locationsFiltered.filter( location =>
            idList.includes(location.id)
          )];
        } else if (this.movimiento === 'actualizar') {
          // Id´s de las ubicaciones de la salida
          const outputLocationIdList: number[] = [];
          if (this.outputDetail[0] && Array.isArray(this.outputDetail[0].location_list)) {
            this.outputDetail[0].location_list.forEach(location => {
              outputLocationIdList.push(location.location_id);
            });
          }
          this.locationsFiltered = [...this.locationsFiltered.filter( location =>
            idList.includes(location.id) || outputLocationIdList.includes(location.id)
          )];
        }
      }
    }
  }

  ngOnInit(): void {
    if (this.movimiento !== 'crear') {
      this.containerStyles['grid-template-columns'] = this.isOutput ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)';

      // La cantidad de items dentro del array -1 porque ya esta creado
      const outputLength = this.outputDetail[0] && Array.isArray(this.outputDetail[0].location_list) ? this.outputDetail[0].location_list.length - 1 : 0;
      const entryLength = this.entryDetail[0] && Array.isArray(this.entryDetail[0].location_list) ? this.entryDetail[0].location_list.length - 1 : 0;
      const locationListLength = this.isOutput ? outputLength : entryLength ;
      for (let index = 0; index < locationListLength; index++) {
        this.addLocation();
      }
      if (this.isOutput) {
        this.locationForm.reset({
          ...this.outputDetail[0]
        });
        this.changeLocation();
      } else {
        this.locationForm.reset({
          ...this.entryDetail[0]
        });
      }
      this.setAmountsToBeAllocated();
      this.setLocationsArray();

      const exportOrCabotage: boolean = this.router.url.includes('cabotage') || this.router.url.includes('exportation');
      // Para deshabilitar los campos de la ubicacion no debe de cumplir con la siguiente validacion
      // que cubre los siguientes puntos:
      // Si la ruta es exportacion o cabaje
      // La accion que se esta realizando es actualizar
      // No hay valor en la ubicacion de la entrada/salida(porque se creo antes de la funcionalidad)
      const validateLocationValue = exportOrCabotage && this.movimiento === 'actualizar' && !(this.locationArray.controls[0] as FormGroup).get('id')?.value;
      if (!validateLocationValue) {
        // Deshabilitar la ubicacion cuando se actualiza solo si locationsStatus es false
        if (this.locationsStatus) {
          this.locationArray.value.forEach((location: any, i: number) => {
            this.locationForm.get(`location_list.${i}.location_id`)!.disable();
          });
        }
      }

      // Agregar el array en caso de que se actualice sin realizar ningun cambio
      this.amountsPerYardSvc.locationsList = [
        ...this.locationArray.value.map((location: any, i: number) => ({
          ...location,
          pieces: this.locationForm.get(`location_list.${i}.pieces`)!.value,
          location_id: this.locationForm.get(`location_list.${i}.location_id`)!.value,
        }))
      ];
    }

    if (this.movimiento === 'detalle') this.locationForm.disable();

    this.locationArray.valueChanges.pipe(
      debounceTime(500)
    ).subscribe( () => {
      this.setLocationsArray();
      this.setAmountsToBeAllocated();
      this.amountsPerYardSvc.locationsList = [
        ...this.locationArray.value.map((location: any, i: number) => ({
          ...location,
          pieces: this.locationForm.get(`location_list.${i}.pieces`)!.value,
          location_id: this.locationForm.get(`location_list.${i}.location_id`)!.value,
        }))
      ];
      this.amountsPerYardSvc.assignedTons = (Number(this.amountsToBeAllocated.tons) === 0 && Number(this.amountsToBeAllocated.tonsPercentage) === 0) ? true : false;
      this.amountsPerYardSvc.assignedPieces = (Number(this.amountsToBeAllocated.pieces) === 0 && Number(this.amountsToBeAllocated.piecesPercentage) === 0) ? true : false;
    });
  }

  /**
   * Agrega un nuevo grupo de ubicación al arreglo del formulario.
   */
  addLocation() {
    this.locationArray.push(this.createLocationGroup());
  }

  /**
   * Crea un grupo de formulario con los campos y validadores de una ubicación.
   * @returns El `FormGroup` que representa una ubicación.
   */
  createLocationGroup(): FormGroup {
    return this.fb.group({
      id: [],
      location_id: ['', [Validators.required]],
      location_tonnage: [{ value: null, disabled: true }],
      location_pieces: [{ value: null, disabled: true }],
      tonnage: [, [Validators.required, Validators.pattern(/^\d+(\.\d{1,3})?$/)]],
      pieces: [{ value: 0, disabled: Number(this.entryPieces) === 0 }, [Validators.required]]
    });
  }

  /**
   * Indica si hay más de una ubicación con identificador, condición para permitir su eliminación.
   * @returns `true` si se puede eliminar una ubicación; `false` en caso contrario.
   */
  canDelete() {
    let yardsWithId = 0;
    this.locationArray.value.forEach((location: any) => {
      if (location.id) yardsWithId++;
    });
    return yardsWithId > 1 ? true : false;
  }

  /**
   * Elimina la ubicación en el índice indicado, gestionando el caso de actualización con borrado en servidor.
   * @param index - Índice de la ubicación a eliminar dentro del arreglo del formulario.
   */
  deleteLocation(index: number) {
    const yardId = Number(this.locationForm.get(`location_list.${index}.id`)?.value);
    if (this.movimiento === 'actualizar' && yardId) {
      if (!this.canDelete()) return;
      this.deleteYard(yardId, index);
      return;
    }
    if (this.locationArray.length >= 2) this.locationArray.removeAt(index);
  }

  /**
   * Elimina una ubicación (patio) en el servidor y la quita del formulario al confirmarse.
   * @param id - Identificador del patio a eliminar.
   * @param index - Índice del patio dentro del arreglo del formulario.
   */
  deleteYard( id: number, index: number ) {
    this.loading = true;
    this.amountsPerYardSvc.deleteYard(id).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.error) {
          this.message = 'Lo sentimos no fue posible eliminar la ubicación, inténtelo más tarde';
          this.icon = 'bi bi-exclamation-circle-fill';
          this.overlay = true;
          setTimeout(() => {
            this.overlay = false;
          },2000);
          return;
        }
        this.locationArray.removeAt(index);
      }, error: () => {
        this.loading = false;
        this.message = 'Error en el servidor al eliminar la ubicación, inténtelo más tarde';
        this.icon = 'bi bi-exclamation-circle-fill';
        this.overlay = true;
        setTimeout(() => {
          this.overlay = false;
        },3000);
      }
    });
  }

  /**
   * Calcula el tonelaje y las piezas pendientes por asignar, junto con sus porcentajes.
   */
  setAmountsToBeAllocated() {
    const entryTons = Number(this.entryTonnage);
    const entryPieces = Number(this.entryPieces);
    const tonsAllocated = this.locationArray.value.reduce( (total: number, location: any) => {
      if (location.tonnage && Number(location.tonnage) > 0) return Number(location.tonnage) + total
      return total
    }, 0);
    const piecesAllocated = this.locationArray.value.reduce( (total: number, location: any, i: number) => {
      const pieces = this.locationForm.get(`location_list.${i}.pieces`)!.value;
      if (pieces && Number(pieces) > 0) return Number(pieces) + total
      return total
    }, 0);
    this.amountsToBeAllocated.tons = entryTons - tonsAllocated;
    this.amountsToBeAllocated.pieces = entryPieces - piecesAllocated;
    this.amountsToBeAllocated.tonsPercentage = entryTons !== 0 ?
      Number(((this.amountsToBeAllocated.tons * 100) / entryTons).toFixed(2)) : 0;
    this.amountsToBeAllocated.piecesPercentage = entryPieces !== 0 ?
      Number(((this.amountsToBeAllocated.pieces * 100) / entryPieces).toFixed(2)) : 0;
  }

  /**
   * Marca como deshabilitadas las ubicaciones que ya fueron seleccionadas en el formulario.
   */
  setLocationsArray() {
    const locationsIdSelected: number[] = [];
    this.locationArray.value.forEach((location: any, i: number) => {
      const location_id = this.locationForm.get(`location_list.${i}.location_id`)!.value;
      if (location_id) {
        locationsIdSelected.push(Number(location_id));
      }
    });
    this.locationsFiltered = this.locationsFiltered.map( location => {
      return {
        ...location,
        disabled: locationsIdSelected.includes(location.id) ? true : false
      };
    });
  }

  /**
   * Asigna automáticamente el tonelaje y las piezas de entrada cuando solo existe una ubicación.
   */
  setTonnageAndPieces() {
    if (this.locationArray.value.length === 1) {
      this.locationForm.get('location_list.0.tonnage')!.setValue(Number(this.entryTonnage).toFixed(3));
      this.locationForm.get('location_list.0.pieces')!.setValue(this.entryPieces);
    }
  }

  /**
   * Carga el tonelaje y las piezas disponibles de cada ubicación seleccionada en el formulario.
   */
  changeLocation() {
    this.locationArray.value.map((location: any, i: number) => {
      const locationId = Number(this.locationForm.get(`location_list.${i}.location_id`)!.value);
      const amountLocation = this.amountsPerYardList.find(yard => yard.id === locationId);
      const locationTonnage = amountLocation ? amountLocation.tonnage : 0;
      const locationPieces = amountLocation ? amountLocation.pieces : 0;
      this.locationForm.get(`location_list.${i}.location_tonnage`)!.setValue(locationTonnage);
      this.locationForm.get(`location_list.${i}.location_pieces`)!.setValue(locationPieces);
    });
  }

  /**
   * Indica si un campo de una ubicación del arreglo es inválido y ha sido tocado.
   * @param index - Índice de la ubicación dentro del arreglo del formulario.
   * @param fieldName - Nombre del campo a evaluar.
   * @returns `true` si el campo tiene errores y fue tocado; en caso contrario `null` o `false`.
   */
  isValidArray( index: number, fieldName: string ): boolean | null {
    return this.locationForm.get(`location_list.${index}.${fieldName}`)!.errors && this.locationForm.get(`location_list.${index}.${fieldName}`)!.touched
  }

  /**
   * Marca todos los controles como tocados y comprueba si el formulario es inválido.
   * @returns `true` si el formulario es inválido; `false` en caso contrario.
   */
  isInvalidForm(): boolean {
    this.locationForm.markAllAsTouched();
    return this.locationForm.invalid;
  }

  /**
   * Muestra un overlay con mensaje e ícono durante el tiempo indicado.
   * @param message - Mensaje a mostrar.
   * @param icon - Clase del ícono a mostrar.
   * @param time - Tiempo en milisegundos antes de ocultar el overlay.
   */
  handleOverlay(message: string, icon: string, time: number) {
    this.overlay = true;
    this.message = message;
    this.icon = icon;
    setTimeout(() => {
      this.overlay = false;
    }, time);
  }
}
